import { Injectable, signal } from '@angular/core';
// import { EventService } from 'app/modules/events/services/event.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { AppSettings } from '../app.settings';
import type { Country } from '../models/country';
import type { CreateEntityPayload } from '../models/entity.models';
import { ConfigService } from './config.service';
import { RestApiService } from './rest-api.service';
import { MessageService } from 'primeng/api';
import * as _ from 'lodash';
import { CommonBindingDataService } from './common-binding-data.service';
@Injectable({
  providedIn: 'root'
})
export class EntityService {
  country: Country;

  private entityView: { [key: string]: { [key: string]: any } } = {};
  private filterState: any = {};
  private relatedData = signal<any>({});
  private bookingStatuses: any = [];
  private transportationTypes: any = [];
  private passengerTypes: any = [];
  public defaultAddressSelection = new BehaviorSubject({});

  constructor(private restApiService: RestApiService, private confService: ConfigService, private messageService: MessageService, private cs: CommonBindingDataService,
    // private eventService: EventService
  ) { }

  entityCurrentRow = new BehaviorSubject(null);


  get getRelateData() {
    return this.relatedData();
  }


  setRelatedData(newString: any): void {
    this.relatedData.set(newString);
  }

  setMiState(key: string, state: any): void {
    this.filterState[key] = state;
  }

  getMiState(key: string): any {
    return this.filterState[key];
  }

  clearMiState(key: string): void {
    delete this.filterState[key];
  }

  setBookingStatuses(bookingStatuses) {
    this.bookingStatuses = bookingStatuses;
  }

  getBookingStatuses() {
    return this.bookingStatuses;
  }

  setTransportationType(transportationTypes) {
    this.transportationTypes = transportationTypes;
  }

  getTransportationType(): [] {
    return this.transportationTypes;
  }

  setPassengerTypes(passengertypes) {
    this.passengerTypes = passengertypes;
  }

  getPassengerTypes(): [] {
    return this.passengerTypes;
  }


  setEntityView(entityName: string, view: string, state: any) {
    if (!this.entityView[entityName]) {
      this.entityView[entityName] = {};
    }
    this.entityView[entityName][view] = state;
  }

  getCachedEntityView(entityName: string, view: string) {
    if (this.entityView[entityName] && this.entityView[entityName][view] !== undefined) {
      return this.entityView[entityName][view];
    }
    return null;
  }

  cleanEntityView(entityName: string, view: string) {
    if (this.entityView[entityName]) {
      delete this.entityView[entityName][view];
      if (Object.keys(this.entityView[entityName]).length === 0) {
        delete this.entityView[entityName];
      }
    }
  }

  getSortView(entity) {
    return new Observable(observer => {
      this.restApiService.get(
        'ATTRIBUTE DEFINITION',
        `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entity}/attributes?forTenantCode=${this.confService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        'page-center'
      ).subscribe({
        next: (response) => {
          observer.next(response);
          observer.complete();
        },
        error: (error) => observer.error(error)
      });
    });
  }

  getAttributeDefinationWithProtocol(entityCode: string, viewCode: string, data: any, loader?: boolean): Observable<any> {
    return this.restApiService.post('ATTRIBUTE DEFINITION', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/selected-related-entities`, data, loader ? 'page-center' : '');
  }


  getAttributeDefinition(entityCode: string, viewCode: string, loader?: boolean): Observable<any> {

    const cachedData = this.getCachedEntityView(entityCode, `attributes_${viewCode}`);
    if (cachedData) {
      return new Observable(observer => {
        observer.next(cachedData);
        observer.complete();
      });
    }


    return new Observable(observer => {
      this.restApiService.get(
        'ATTRIBUTE DEFINITION',
        `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes?forTenantCode=${this.confService.getForTenantCode()}&viewCode=${viewCode}`, loader ?
        'page-center' : ''
      ).subscribe({
        next: (response) => {
          this.setEntityView(entityCode, `attributes_${viewCode}`, response);
          observer.next(response);
          observer.complete();
        },
        error: (error) => observer.error(error)
      });
    });
  }

  getSiteProtocols(data: any, loader?: boolean) {
    return this.restApiService.post('SITE PROTOCOLS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/custom/sites/protocols`, data, loader ? 'page-center' : '');
  }

  getProtocolUser(protocolId: string) {
    return this.restApiService.get('PROTOCOL USER', `${this.confService.getLoggedInTenantCode()}/api/v1/users/protocol-user/${protocolId}?forTenantCode=${this.confService.getForTenantCode()}`, 'page-center');
  }

  saveAttributeData(entityCode: string, entityId: string, data: object) {
    return this.restApiService.post('SAVE ATTRIBUTE DEFINITION', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/${entityId}/attribute-data`, data, 'page-center');
  }

  saveDriverDutyStatusData(entityCode: string, data) {
    this.setEventCode(data);
    return this.restApiService.post('SAVE DRIVER DUTY STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/update-duty-status`, data, 'page-center');
  }

  createEntities(entityCode: string, data: CreateEntityPayload) {
    return this.restApiService.post('CREATE NEW ENTITY', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities`, data, 'page-center');
  }

  getEntityCount(entityCode: string, data: any, loader?: boolean) {
    this.setEventCode(data);
    return this.restApiService.post('CREATE ENTITIES', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/counts`, data, loader ? 'page-center' : '');
  }

  searchEntity(entityCode: string, data: any, loader?: boolean) {
    this.setEventCode(data);
    return this.restApiService.post('SEARCH ENTITY', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/search`, data, loader ? 'page-center' : '');
  }

  searchEntityForVehicleTransfer(data) {
    this.setEventCode(data);
    return this.restApiService.post('SEARCH VEHICLE TRANSFER', `${this.confService.getLoggedInTenantCode()}/api/v1/custom/vehicle-transfer-list`, data, 'page-center');
  }

  searchEntityForTags(entityCode: string, data, loader?: boolean) {
    return this.restApiService.post('SEARCH ENTITY FOR TAGS', `${this.confService.getLoggedInTenantCode()}/api/v1/${entityCode}/search?forTenantCode=${this.confService.getForTenantCode()}`, data, loader ? 'page-center' : '');
  }

  searchEntityForUsage(data, loader?: boolean) {
    return this.restApiService.post('SEARCH ENTITY FOR USAGE', `${this.confService.getLoggedInTenantCode()}/api/v1/usages/count`, data, loader ? 'page-center' : '');
  }

  searchSiteProtocols(data: any, loader?: boolean) {
    return this.restApiService.post('SEARCH SITE PROTOCOLS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/custom/sites/protocols`, data, loader ? 'page-center' : '');
  }


  getEntityDashboardCount(entityCode: string, entityId: string, loader?: boolean) {
    return this.restApiService.get('SEARCH ENTITY FOR USAGE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/dashboard-count/${entityId}?forTenantCode=${this.confService.getForTenantCode()}`, loader ? 'page-center' : '');
  }

  getDashboardProductSoaCount(data: { forTenantCode: string; protocolId: string; siteIds?: string[] }, loader?: boolean) {
    return this.restApiService.post('DASHBOARD PRODUCT SOA COUNT', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/custom/dashboard/count/product-soa`, data, loader ? 'page-center' : '');
  }

  getDashboardCount(data: { forTenantCode: string }, loader?: boolean) {
    return this.restApiService.get('DASHBOARD COUNT', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/custom/dashboard/count?forTenantCode=${data.forTenantCode}`, loader ? 'page-center' : '');
  }

  getProtocolForecastReport(data: { tenantCode: string; protocolId: string; siteId?: string }, loader?: boolean) {
    return this.restApiService.post('PROTOCOL FORECAST REPORT', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/protocol/custom/protocol-forecast-report`, data, loader ? 'page-center' : '');
  }

  getEntity(entityId: string, entityCode: string, viewCode: string) {
    return this.restApiService.get('GET ENTITY DETAILS BY ENTITYID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/${entityId}?forTenantCode=${this.confService.getForTenantCode()}&viewCode=${viewCode}`, 'page-center');
  }

  getEntityForChangeReview(entityId: string, entityCode: string) {
    return this.restApiService.get('GET ENTITY DETAILS BY PROTOCOL REVIEW REQUEST ID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/review-requests/${entityId}?forTenantCode=${this.confService.getForTenantCode()}`, 'page-center');
  }

  getEntityDetailsByEntityIds(data: any, entityCode: string) {
    this.setEventCode(data);
    return this.restApiService.post('GET ENTITY DETAILS BY ENTITYIDS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/selected-list`, data, 'page-center');
  }

  downloadImportTemplate(entityCode: string): Observable<any> {
    return this.restApiService.downloadDocument(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entity-files/template?forTenantCode=${this.confService.getForTenantCode()}`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }
  exportOrdersInvoiceData(entityCode: string, data: any): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentPostWithHeaders(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-invoice`, data);
  }

  exportOrderErpData(entityCode: string, data: any): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentPostWithHeaders(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-erp-export`, data);
  }

  exportSingleOrder(entityCode: string, orderId: string): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentWithHeaders(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-invoice/${orderId}?forTenantCode=${this.confService.getForTenantCode()}`, 'application/json');
  }
  // downloadBarcode(entityId, entityCode: string): Observable<any> {
  //   return this.restApiService.downloadDocumentBarcode(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/${entityId}/barcode?forTenantCode=${this.confService.getForTenantCode()}&countryCode=IN&eventCode=${this.eventService.getSelectedEventcode()}`);
  // }

  printLabels(entityCode, data): Observable<any> {
    this.setEventCode(data);
    return this.restApiService.downloadDocumentPost(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/print-labels`, data);
  }


  downloadImportGuide(entityCode: string): Observable<any> {
    return this.restApiService.downloadDocument(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entity-files/guide?forTenantCode=${this.confService.getForTenantCode()}`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }
  deleteEntityActionStatus(data) {
    this.setEventCode(data);
    return this.restApiService.put('DELETE ENTITIES', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${data.entityCode}/entities/bulk-delete`, data, 'page-center');
  }

  updateBulkData(data) {
    this.setEventCode(data);
    return this.restApiService.put('BULK UPDATE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${data.entityCode}/entities/bulk-update`, data, 'page-center');
  }

  getFile(fileId, fileType, thumb = 'yes') {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    return this.restApiService.downloadDocument(`${this.confService.getLoggedInTenantCode()}/api/v1/files/${fileType}/download/${fileId}?forTenantCode=${this.confService.getForTenantCode()}&thumb=${thumb}&X-COUNTRY-CODE=${this.country[0].countryCode}`, 'application/json', 'blob');
  }

  updateStatus(entityId, data) {
    this.setEventCode(data);
    return this.restApiService.put('ACTION-STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${data.entityCode}/entities/${entityId}/action-status`, data, 'page-center');
  }

  assignDriverGroup(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('DRIVER GROUP', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/assign-driver-group`, data, 'page-center');
  }

  releaseDriverGroup(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('RELEASE DRIVER GROUP', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/release-driver-group`, data, 'page-center');
  }

  assignLocation(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('ASSIGN LOCATION', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/assign-location`, data, 'page-center');
  }

  releaseLocation(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('RELEASE LOCATION', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/release-location`, data, 'page-center');
  }

  assignVehicle(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('ASSIGN VEHICLE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/assign-vehicle`, data, 'page-center');
  }

  assignDriver(data, entityCode) {
    this.setEventCode(data);
    return this.restApiService.post('ASSIGN DRIVER', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/booking-assign-driver`, data, 'page-center');
  }

  releaseVehicle(entityCode, data) {
    this.setEventCode(data);
    return this.restApiService.post('RELEASE VEHICLE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/release-vehicle`, data, 'page-center');
  }

  autoComplete(entityCode, data): Observable<any> {
    this.setEventCode(data);
    return this.restApiService.post('AUTO COMPLETE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/auto-complete`, data, 'page-center');
  }

  distinctValues(entityCode, data): Observable<any> {
    return this.restApiService.post('AUTO COMPLETE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/distinct-attribute-values`, data, 'page-center');
  }

  updateIncidentStatus(entityCode: string, data: object) {
    return this.restApiService.post('UPDATE INCIDENT STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/update-incident-status`, data, 'page-center');
  }

  updateEventStatus(entityCode: string, data: object) {
    this.setEventCode(data);
    return this.restApiService.post('UPDATE EVENT STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/update-event-status`, data, 'page-center');
  }

  searchEntityHeader(entityCode: string, entityData: any): Observable<any> {
    if (this.confService.getLoggedInTenantCode()) {
      const url = `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/search`;
      return this.restApiService.post('SEARCH ENTITY', url, entityData, 'page-center');
    }
  }



  addOrganization(requestBody) {
    return this.restApiService.post('ADD ORGANIZATION', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants`, requestBody, 'page-center');
  }

  updateOrganization(requestBody, tenantId) {
    return this.restApiService.put('UPDATE ORGANIZATION', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants/${tenantId}`, requestBody, 'page-center');
  }

  getEntityConfiguration(requestBody, tenantCode) {
    return this.restApiService.post('ADD ORGANIZATION', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-configurations/search?forTenantCode=${tenantCode}`, requestBody, 'page-center');
  }

  getOrganizationList(requestBody) {
    return this.restApiService.post('ORGANIZATION LIST', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants/search`, requestBody, 'page-center');
  }

  getOrgDetails(tenantId: string) {
    return this.restApiService.get('GET TENANT DETAILS BY TENANT ID', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants/${tenantId}`, 'page-center');
  }

  getUserDetails(userId: string, forTenantCode) {
    return this.restApiService.get('GET USER BY ID', `${this.confService.getLoggedInTenantCode()}/api/v1/users/${userId}?forTenantCode=${forTenantCode}`, 'page-center');
  }

  updateOrganizationStatus(tenantId: string, data: any) {
    return this.restApiService.put('Update Organization Status', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants/${tenantId}/action`, data, 'page-center');
  }

  addAttributeSettings(entityCode: string, data: any) {
    return this.restApiService.post('ADD ATTRIBUTE SETTINGS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/preset-values`, data, 'page-center');
  }

  getEntityDetailsByView(entityCode: string, data, loader?: boolean) {
    return this.restApiService.post('get entity details by view', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/find-by-view?forTenantCode=${this.confService.getForTenantCode()}&eventCode=null`, data, loader ? 'page-center' : '');
  }

  getEntityDetailsById(entityCode: string, data: any) {
    this.setEventCode(data);
    return this.restApiService.post('get entity details by Id', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/find-by-view?forTenantCode=${this.confService.getForTenantCode()}`, data, 'page-center');
  }

  editAttributeSettings(entityCode: string, presetLabelId, data: any) {
    return this.restApiService.put('EDIT ATTRIBUTE SETTINGS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/preset-values/${presetLabelId}`, data, 'page-center');
  }

  searchAttributeSettings(entityCode: string, data: any, loader?: boolean) {
    return this.restApiService.post('SEARCH ATTRIBUTE SETTINGS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/preset-values/search`, data, loader ? 'page-center' : '');
  }

  deleteAttributeSettings(entityCode: string, data) {
    return this.restApiService.put('DELETE ATTRIBUTE SETTINGS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/attributes/preset-values/action/bulk-delete`, data, 'page-center');
  }

  saveAccessToModules(data) {
    return this.restApiService.put('save access to modules', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-configurations/bulk-update`, data, 'page-center');
  }

  getTimeZones(data, loader?: boolean) {
    return this.restApiService.post('get time zones', `${this.confService.getLoggedInTenantCode()}/api/v1/timezones`, data, loader ? 'page-center' : '');
  }

  getLanguages(data, loader?: boolean) {
    return this.restApiService.post('get languages', `${this.confService.getLoggedInTenantCode()}/api/v1/languages/search?forTenantCode=${this.confService.getForTenantCode()}`, data, loader ? 'page-center' : '');
  }

  updateDriverStatus(data) {
    this.setEventCode(data);
    return this.restApiService.post('ACTION-STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${data.entityCode}/custom/update-driver-status`, data, 'page-center');
  }

  getPresetLabels(data, entityCode) {
    return this.restApiService.post('get preset labels', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/preset-labels/search`, data, 'page-center');
  }

  getTagsList(data) {
    return this.restApiService.post('get tags', `${this.confService.getLoggedInTenantCode()}/api/v1/tags/search?forTenantCode=${this.confService.getForTenantCode()}`, data, 'page-center');
  }

  getCommonPresetLabels(data) {
    return this.restApiService.post('get common preset labels', `${this.confService.getLoggedInTenantCode()}/api/v1/common-preset-labels/search`, data, 'page-center');
  }

  getUserDetailsById(data) {
    return this.restApiService.post('GET USER DETAILS BY ID', `${this.confService.getLoggedInTenantCode()}/api/v1/users/user-details?forTenantCode=${this.confService.getForTenantCode()}`, data, 'page-center');
  }

  getReverseGeoCodeUsingLatLng(latLng) {
    return this.restApiService.get('GET REVERSE LAT LNG', `${this.confService.getLoggedInTenantCode()}/api/v1/location-info/reverse-geocode?forTenantCode=${this.confService.getForTenantCode()}&latLng=${latLng}`, 'page-center');
  }

  getUniqueIdentificationByTenant() {
    return this.restApiService.get('UNIQUE IDENTIFICATION BY TENANT', `${this.confService.getLoggedInTenantCode()}/api/v1/tenants/unique-ids?forTenantCode=${this.confService.getForTenantCode()}`, 'page-center');
  }

  getEntityDetailsByUniqueId(data, entityCode) {
    return this.restApiService.post('GET ENTITY DETAILS BY UNIQUE ID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/details-other-events`, data, 'page-center');
  }

  googlemapAutoCompleteApi(input, location) {
    return this.restApiService.get('google map autocomplete', `${this.confService.getLoggedInTenantCode()}/api/v1/location-info/autocomplete?forTenantCode=${this.confService.getForTenantCode()}&input=${input}&location=${location}`, 'page-center');
  }

  // getDriverCurrentLocationById(entityCode, entityId) {
  //   return this.restApiService.get('GET DRIVER CURRENT LOCATION BY ID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/driver-location/${entityId}?forTenantCode=${this.confService.getForTenantCode()}&eventCode=${this.eventService.getSelectedEventcode()}`, 'page-center');
  // }

  // getVehicleLocationById(entityCode, entityId) {
  //   return this.restApiService.get('GET VEHICLE LOCATION BY ID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/vehicles?forTenantCode=${this.confService.getForTenantCode()}&eventCode=${this.eventService.getSelectedEventcode()}&vehicleEntityId=${entityId}`, 'page-center');
  // }

  resetPrivilegeSettings(entityCode: string, data: any, loader?: boolean) {
    return this.restApiService.post('RESET PRIVILEGE SETTINGS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/reset-privileges`, data, loader ? 'page-center' : '');
  }

  driverCurrentShift(data: any, loader?: boolean) {
    return this.restApiService.post('DRIVER CURRENT SHIFT', `${this.confService.getLoggedInTenantCode()}/api/v1/shift-calendars/driver/current-shifts`, data, loader ? 'page-center' : '');
  }

  // passengerPrivilegeDetailsByPassengerId(entityCode, entityId) {
  //   return this.restApiService.get('GET PASSENGER PRIVILEGE DETAILS BY ID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/passenger-privileges/${entityId}?forTenantCode=${this.confService.getForTenantCode()}&eventCode=${this.eventService.getSelectedEventcode()}`, 'page-center');
  // }

  selectedEntityListInBulk(data, entityCode, loader?: boolean) {
    return this.restApiService.post('GET SELECTED ENTITY LIST IN BULK', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/selected-list`, data, loader ? 'page-center' : '');
  }

  private setEventCode(data) {
    // data.eventCode = this.eventService.getSelectedEventcode();
    return data;
  }

  getSettingsEntity(entityId: string, entityCode: string, viewCode: string, loader?: boolean) {
    return this.restApiService.get('GET ENTITY DETAILS BY ENTITYID', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/entities/${entityId}?forTenantCode=${this.confService.getForTenantCode()}&viewCode=${viewCode}`, loader ? 'page-center' : '');
  }

  searchEntityChangeReview(entityCode: string, data: any, loader?: boolean) {
    this.setEventCode(data);
    return this.restApiService.post('SEARCH ENTITY', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/review-requests/search`, data, loader ? 'page-center' : '');
  }

  searchEntityAuditLogs(entityCode: string, data: any, loader?: boolean) {
    return this.restApiService.post('SEARCH ENTITY', `${this.confService.getLoggedInTenantCode()}/api/v1/audit-logs/search?forTenantCode=${this.confService.getForTenantCode()}`, data, loader ? 'page-center' : '');
  }

  exportAuditLogs(entityCode: string, entityData: any, selectedColumns: any) {
    const data = {
      auditPaginationDto: _.clone(entityData),
      columns: []
    }

    data.columns = _.map(selectedColumns, (item) => {
      if (item.attributeCode === "Id") {
        return {
          label: "ID",
          key: "auditLogId",
          converter: ""
        };
      }

      return {
        label: item.header,
        key: item.attributeCode,
        converter: ""
      };
    });

    this.export(data).subscribe(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Success! Your file has been successfully exported. Check your email for a link to download the Excel file.'
      });
    },
      () => {
      });
  }

  export(data: any): Observable<any> {
    return this.restApiService.post('EXPORT AUDIT LOGS', `${this.confService.getLoggedInTenantCode()}/api/v1/audit-logs/export-audit-logs?forTenantCode=${this.confService.getForTenantCode()}`, data, 'page-center');
  }


  updateReviewStatus(entityCode: string, data: any) {
    return this.restApiService.put('UPDATE REVIEW STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/review-requests/action`, data);
  }

  statusOrder(data: any, loader?: boolean) {
    return this.restApiService.post('CONFIRM ORDER STATUS', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/order/custom/order-status-update?forTenantCode=${this.confService.getForTenantCode()}`, data, loader ? 'page-center' : '');
  }

  acceptSiteOrder(entityCode, data: any, loader?: boolean) {
    return this.restApiService.post('SITE ADMIN ORDER ACCEPTANCE', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-acceptance`, data, loader ? 'page-center' : '');
  }

  reviewSpecialRequest(data: {
    forTenantCode: string;
    countryCode: string;
    languageCode: string;
    bookingEntityId: string;
    passengerVehicleCount: number;
    luggageVehicleCount: number;
  }) {
    this.setEventCode(data);
    return this.restApiService.post(
      'REVIEW SPECIAL REQUEST',
      `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/booking/custom/review-special-request`,
      data,
      'page-center'
    );
  }

  downloadReturnOrderReport(entityCode: string, orderId: string): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentWithHeaders(
      `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/return-order-pdf/${orderId}?forTenantCode=${this.confService.getForTenantCode()}`,
      'application/pdf'
    );
  }

  markOrderCompleteAndSendEmail(entityCode: string, data: any): Observable<any> {
    this.setEventCode(data);
    return this.restApiService.post('MARK ORDER COMPLETE AND SEND EMAIL', `${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/mark-order-complete`, data, 'page-center');
  }

  exportOrderReport(entityCode: string, data: any): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentPostWithHeaders(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-export`, data);
  }

  exportOrderAksysReport(entityCode: string, data: any): Observable<HttpResponse<any>> {
    return this.restApiService.downloadDocumentPostWithHeaders(`${this.confService.getLoggedInTenantCode()}/api/v1/entity-conf/${entityCode}/custom/order-aksys-export`, data);
  }
}
